import React from 'react';
import { LuLoaderPinwheel } from 'react-icons/lu';

import { StandaloneChangeField, StandaloneChangelogEntry } from '../../entities/standalone-changelog';
import { AgentAuthorBadge } from '../ui/AgentAuthorBadge';

const FIELD_LABELS: Record<StandaloneChangeField, string> = {
  title: 'Title',
  description: 'Description',
  status: 'Status',
  priority: 'Priority',
  dueDate: 'Due date',
  parentId: 'Parent task',
  labelIds: 'Labels',
};

// description/labelIds carry full untruncated values (MVPB-25) — collapse long diffs so one edit
// doesn't blow out the whole feed; the modal's underlying data still has the full text.
const MAX_VALUE_LEN = 80;
const formatValue = (value: string) => {
  if (!value.trim()) return '(none)';
  return value.length > MAX_VALUE_LEN ? `${value.slice(0, MAX_VALUE_LEN)}…` : value;
};

interface ChangelogFeedProps {
  entries: StandaloneChangelogEntry[];
  isLoading?: boolean;
}

// Read-only activity feed for a standalone task — "who changed what, when." Reuses
// AgentAuthorBadge (action="changed") for MCP-attributed edits, same as MVPB-47's task/comment
// badges. No live-update wiring yet: unlike comments, there's no `standaloneChangelogUpdated`
// socket event in the BE contract, so this only reflects what was loaded on open.
export const ChangelogFeed: React.FC<ChangelogFeedProps> = ({ entries, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex justify-center py-2">
        <LuLoaderPinwheel className="animate-spin opacity-40" size={16} />
      </div>
    );
  }

  if (entries.length === 0) {
    return <div className="text-sm opacity-40 py-2">No activity yet.</div>;
  }

  // API returns oldest first — newest-first reads more naturally as an activity feed.
  const ordered = [...entries].reverse();

  return (
    <div className="space-y-1.5 max-h-48 overflow-y-auto">
      {ordered.map((entry) => (
        <div key={entry._id} className="rounded-lg bg-base-200 px-3 py-2 text-sm space-y-1">
          <div className="flex items-center gap-1.5">
            {entry.authorSource === 'mcp_agent' && <AgentAuthorBadge clientName={entry.clientName} action="changed" />}
            <span className="text-xs opacity-40">{new Date(entry.createdAt).toLocaleString()}</span>
          </div>
          <ul className="space-y-0.5">
            {entry.items.map((item, i) => (
              <li key={i} className="text-xs opacity-80">
                {item.field === 'parentId' ? (
                  `${FIELD_LABELS.parentId} changed`
                ) : (
                  <>
                    <span className="font-medium">{FIELD_LABELS[item.field]}:</span> {formatValue(item.fromString)} →{' '}
                    {formatValue(item.toString)}
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};
